export type Maybe<T> = T | null;
export type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** All built-in and custom scalars, mapped to their actual values */
export type Scalars = {
  ID: string;
  String: string;
  Boolean: boolean;
  Int: number;
  Float: number;
  /** HTML content */
  HTML: string;
  /** Language code, e.g. en, fr, de, my, etc */
  LanguageID: string;
  /** Non-negative integer */
  NonNegativeInt: number;
  /** Positive integer */
  PositiveInt: number;
  /** Uniform Resource Locator */
  URL: string;
  /** The `Long` scalar type represents non-fractional signed whole numeric values. Long can represent values between -(2^63) and 2^63 - 1. */
  Long: number;
};

/** Content access control */
export type AccessControl = {
  __typename?: 'AccessControl';
  /** Access level */
  access: AccessLevel;
  /** Whether the article is for internal use */
  isInternal: Scalars['Boolean'];
  /** Smart groups for restricted/targeted content */
  smartGroups: Array<SmartGroup>;
};

/** Defines the content access level */
export enum AccessLevel {
  /** Public */
  Public = 'PUBLIC',
  /** Restricted */
  Restricted = 'RESTRICTED',
  /** Private or hidden */
  None = 'NONE'
}

/** Represents a help article */
export type Article = {
  /** Article identifier */
  id: Scalars['ID'];
  /** Article title */
  title: Scalars['String'];
};

/** Represents a help article when displayed individually */
export type ArticleDetail = Article & {
  __typename?: 'ArticleDetail';
  /** Article identifier */
  id: Scalars['ID'];
  /** Article title */
  title: Scalars['String'];
  /** Article body */
  body: Scalars['HTML'];
  /** Unformatted article body */
  bodyText: Scalars['String'];
  /** When the article was first created in UNIX epoch milliseconds */
  createdAt: Scalars['Long'];
  /** When the article was last updated in UNIX epoch milliseconds */
  lastUpdatedAt: Scalars['Long'];
  /** When the article was last published in UNIX epoch milliseconds */
  lastPublishedAt: Scalars['Long'];
  /** Article's original author */
  author: Author;
  /** List of authors who contributed to the article */
  contributors: Array<Scalars['String']>;
  /** Featured image or icon URL */
  featuredImageUrl?: Maybe<Scalars['String']>;
  /** Article word count */
  wordCount: Scalars['Int'];
  /** Article slug */
  slug: Scalars['String'];
  /** Article summary */
  summary?: Maybe<Scalars['String']>;
  /** Article tags */
  tags: Array<Scalars['String']>;
  /** Access control */
  accessControl: AccessControl;
  /** Article category */
  category: CategoryListResult;
};

/** Represents a query to retrieve articles with different filters */
export type ArticleListQuery = {
  /** Pagination parameters */
  paging: PagingParams;
  /** Include articles with these identifiers */
  ids?: Maybe<Array<Scalars['ID']>>;
  /** Include articles similar to those with these identifiers */
  similarTo?: Maybe<Array<Scalars['ID']>>;
  /** Include articles with any of these tags */
  tags?: Maybe<Array<Scalars['String']>>;
};

/** Represents a help article when displayed in a list */
export type ArticleListResult = Article & {
  __typename?: 'ArticleListResult';
  /** Article identifier */
  id: Scalars['ID'];
  /** Article title */
  title: Scalars['String'];
  /** When the article was last published in UNIX epoch milliseconds */
  lastPublishedAt: Scalars['Long'];
  /** Article slug */
  slug: Scalars['String'];
  /** Article tags */
  tags: Array<Scalars['String']>;
  /** Article category */
  category: CategorySearchResult;
  /** Article summary */
  summary?: Maybe<Scalars['String']>;
};

/** Represents a list of article listing results */
export type ArticleListResultSet = PaginatedResult & {
  __typename?: 'ArticleListResultSet';
  /** Contains pagination parameters */
  pageInfo: PageInfo;
  /** Articles */
  results: Array<ArticleListResult>;
};

/** Represents a query to search articles by search terms */
export type ArticleSearchQuery = {
  /** Pagination parameters */
  paging: PagingParams;
  /** Includes articles that contain these search terms */
  terms: Scalars['String'];
  /** Include only articles with any of these tags */
  tags?: Maybe<Array<Scalars['String']>>;
};

/** Represents a help article when displayed as a search result */
export type ArticleSearchResult = Article & {
  __typename?: 'ArticleSearchResult';
  /** Article identifier */
  id: Scalars['ID'];
  /** Article title */
  title: Scalars['String'];
  /** When the article was last published in UNIX epoch milliseconds */
  lastPublishedAt: Scalars['Long'];
  /** Article slug */
  slug: Scalars['String'];
  /** Article tags */
  tags: Array<Scalars['String']>;
  /** Article category */
  category: CategorySearchResult;
  /** Highlighted snippets matching the query */
  highlights?: Maybe<Highlights>;
};

/** Represents a list of article search results */
export type ArticleSearchResultSet = PaginatedResult & {
  __typename?: 'ArticleSearchResultSet';
  /** Contains pagination parameters */
  pageInfo: PageInfo;
  /** Articles */
  results: Array<ArticleSearchResult>;
};

/** Represents a query to suggest articles for a given long text */
export type ArticleSuggestionQuery = {
  /** Pagination parameters */
  paging: PagingParams;
  /** Includes articles relevant to this long text */
  text: Scalars['String'];
};

/** Represents a help article when displayed as a suggestion */
export type ArticleSuggestionResult = Article & {
  __typename?: 'ArticleSuggestionResult';
  /** Article identifier */
  id: Scalars['ID'];
  /** Article title */
  title: Scalars['String'];
  /** When the article was last published in UNIX epoch milliseconds */
  lastPublishedAt: Scalars['Long'];
  /** Article slug */
  slug: Scalars['String'];
  /** Article tags */
  tags: Array<Scalars['String']>;
  /** Article category */
  category: CategorySearchResult;
  /** Highlighted snippets matching the query */
  highlights?: Maybe<Highlights>;
};

/** Represents a list of article suggestions */
export type ArticleSuggestionResultSet = PaginatedResult & {
  __typename?: 'ArticleSuggestionResultSet';
  /** Contains pagination parameters */
  pageInfo: PageInfo;
  /** Articles */
  results: Array<ArticleSuggestionResult>;
};

/** Assistant settings */
export type Assistant = {
  __typename?: 'Assistant';
  /** Whether the assistant is enabled */
  enabled: Scalars['Boolean'];
  /** Whether the assistant is enabled for authenticated users only */
  loggedInOnly: Scalars['Boolean'];
  /** Whether the assistant remembers the open state */
  autoOpen: Scalars['Boolean'];
  /** How the assistant is rendered */
  displayType: AssistantDisplayType;
  /** Which side of the page the assistant is placed */
  side: AssistantSide;
};

/** Defines how the assistant is rendered */
export enum AssistantDisplayType {
  /** Rendered as full height */
  Full = 'FULL',
  /** Rendered as compressed */
  Compressed = 'COMPRESSED',
  /** Rendered as full height and compressed */
  Pushin = 'PUSHIN'
}

/** Defines the side of the page where the assistant is placed */
export enum AssistantSide {
  /** Placed on the left */
  Left = 'LEFT',
  /** Placed on the right */
  Right = 'RIGHT'
}

/** Represents an article author */
export type Author = {
  __typename?: 'Author';
  /** Author identifier */
  id: Scalars['ID'];
  /** Author's full name */
  name: Scalars['ID'];
  /** Author's avatar URL */
  avatar: Scalars['ID'];
};

/** Represents a help article category */
export type Category = {
  /** Category identifier */
  id: Scalars['ID'];
  /** Category title */
  title: Scalars['String'];
};

/** Represents a help article category when displayed in a list */
export type CategoryListResult = Category & {
  __typename?: 'CategoryListResult';
  /** Category identifier */
  id: Scalars['ID'];
  /** Category title */
  title: Scalars['String'];
  /** Category slug */
  slug: Scalars['String'];
  /** Featured image or icon URL */
  featuredImageUrl?: Maybe<Scalars['String']>;
  /** This category's parent */
  parentCategory?: Maybe<CategoryListResult>;
  /** Sub-categories of this category (direct descendents only) */
  subCategories: Array<CategoryListResult>;
  /** Articles that fall under this category */
  articles: ArticleListResultSet;
};


/** Represents a help article category when displayed in a list */
export type CategoryListResultArticlesArgs = {
  paging?: PagingParams;
};

/** Represents a help article category when displayed as a search result */
export type CategorySearchResult = Category & {
  __typename?: 'CategorySearchResult';
  /** Category identifier */
  id: Scalars['ID'];
  /** Category title */
  title: Scalars['String'];
  /** Category slug */
  slug: Scalars['String'];
  /** This category's parent */
  parentCategory?: Maybe<CategorySearchResult>;
};

/** Company attributes */
export type Company = {
  __typename?: 'Company';
  /** Company UID */
  uid: Scalars['ID'];
  /** Company name */
  name?: Maybe<Scalars['String']>;
  /** Company default language */
  defaultLanguage: Language;
  /** Company supported languages */
  languages: Array<Language>;
  /** Whether this company is on a plan that allows white-label assets */
  whitelabelEnabled: Scalars['Boolean'];
};

/** Defines how a walkthrough step is rendered */
export enum DisplayType {
  /** Rendered as a modal */
  Modal = 'MODAL',
  /** Rendered as a tooltip */
  Tooltip = 'TOOLTIP'
}


/** Represents highlighted snippets matching the query */
export type Highlights = {
  __typename?: 'Highlights';
  /** Title highlights */
  title: Array<Scalars['String']>;
  /** Body highlights */
  body: Array<Scalars['String']>;
};

/** KB Redirects */
export type KbRedirect = {
  __typename?: 'KBRedirect';
  /** Route or regex in KB */
  fromUrl: Scalars['String'];
  /** Target route for redirect */
  toUrl: Scalars['String'];
};

/** Knowledge base settings */
export type KbSettings = {
  __typename?: 'KBSettings';
  /** Subdomain under elevio domain */
  subdomain: Scalars['String'];
  /** Custom domain name */
  cname?: Maybe<Scalars['String']>;
  /** Whether the cname was verified by HQ */
  cnameVerified: Scalars['Boolean'];
  /** Company homepage for header link */
  siteUrl?: Maybe<Scalars['String']>;
  /** Whether this KB is indexed */
  searchEngineIndexed: Scalars['Boolean'];
  /** Custom favicon url */
  favicon?: Maybe<Scalars['String']>;
  /** Whether this KB uses HTTPS/TLS */
  httpsEnabled: Scalars['Boolean'];
  /** Whether this KB uses a custom domain */
  customDomainEnabled: Scalars['Boolean'];
  /** Single sign-on configuration */
  sso: Sso;
  /** Whether to show embeddable elements */
  showEmbeddable: Scalars['Boolean'];
  /** Whether this KB shows search results while the user is typing */
  searchAsYouTypeEnabled: Scalars['Boolean'];
  /** Version UID to load assets from S3 */
  liveVersion?: Maybe<Scalars['ID']>;
  /** Whether this KB restricts access to certain IP ranges */
  whitelistEnabled: Scalars['Boolean'];
  /** Whether this KB allows agents to login to Elevio for access to non-public articles */
  agentLoginEnabled: Scalars['Boolean'];
  /** Feedback form configuration */
  feedbackForm: FeedbackForm;
  /**
   * Whether to hide the email field in the article feedback form
   * @deprecated Replaced by feedbackForm object
   */
  hideArticleFeedbackEmail: Scalars['Boolean'];
  /** Whether this KB uses an external TLS termination system */
  companyManagedTls: Scalars['Boolean'];
};

/** Information about theme used by this KB */
export type KbTheme = {
  __typename?: 'KBTheme';
  /** Theme identifier */
  id: Scalars['ID'];
  /** Identifier of company that created this theme */
  companyId: Scalars['ID'];
  /** Theme name */
  name: Scalars['String'];
  /** UID of live version on S3 */
  liveVersion?: Maybe<Scalars['ID']>;
};

/** Knowledge base settings */
export type KbUserSettings = {
  __typename?: 'KBUserSettings';
  /** Whether to hide the email field in the article feedback form */
  hideArticleFeedbackEmail: Scalars['Boolean'];
};

/** Whitelisted CIDR block for KB access */
export type KbWhitelistEntry = {
  __typename?: 'KBWhitelistEntry';
  /** Whitelisted IP in CIDR format */
  cidrBlock: Scalars['String'];
};

/** Knowledgebase settings and data for KB Server */
export type Knowledgebase = {
  __typename?: 'Knowledgebase';
  /** KB Settings */
  settings: KbSettings;
  /** KB Redirects */
  redirects: Array<KbRedirect>;
  /** Whitelisted CIDR blocks for KB access */
  accessWhitelist: Array<KbWhitelistEntry>;
  /** Information about theme used by this KB */
  theme: KbTheme;
  /** Company attributes */
  company: Company;
};

/** Language attributes */
export type Language = {
  __typename?: 'Language';
  /** Language identifier */
  id: Scalars['LanguageID'];
};



/** Contains output pagination parameters */
export type PageInfo = {
  __typename?: 'PageInfo';
  /** Total number of items fetched */
  totalCount: Scalars['NonNegativeInt'];
  /** Current page being displayed */
  currentPage: Scalars['NonNegativeInt'];
  /** Total number of result pages available */
  totalPages: Scalars['NonNegativeInt'];
};

/** Interface for paginated result sets */
export type PaginatedResult = {
  /** Object containing pagination parameters */
  pageInfo: PageInfo;
};

/** Represents input parameters for pagination */
export type PagingParams = {
  /** Maximum number of results to be fetched */
  first: Scalars['PositiveInt'];
  /** Offset from the first result to be fetched */
  offset: Scalars['NonNegativeInt'];
};

/** Defines positioning options of an element */
export enum PlacementType {
  /** Top */
  T = 'T',
  /** Top left */
  Tl = 'TL',
  /** Top right */
  Tr = 'TR',
  /** Bottom */
  B = 'B',
  /** Bottom left */
  Bl = 'BL',
  /** Bottom right */
  Br = 'BR',
  /** Left */
  L = 'L',
  /** Right */
  R = 'R',
  /** Centre */
  C = 'C'
}

/** Cartesian position offset */
export type PositionOffset = {
  __typename?: 'PositionOffset';
  /** X coordinate offset */
  x: Scalars['Float'];
  /** Y coordinate offset */
  y: Scalars['Float'];
};


export type Query = {
  __typename?: 'Query';
  /** Fetch user-specific settings for a company */
  userSettings: UserSettings;
  /** Fetch knowledgebase settings, redirects and valid languages by domain (cname or subdomain) */
  knowledgebase: Knowledgebase;
  /** Fetch new and in-progress walkthroughs for a user */
  walkthroughs: Array<Walkthrough>;
  /** Fetch articles in a given language */
  articles: ArticleListResultSet;
  /** Search for articles in a given language */
  searchArticles: ArticleSearchResultSet;
  /** Suggest articles for a given language-specific long text */
  suggestArticles: ArticleSuggestionResultSet;
  /** Fetch article given an id */
  article?: Maybe<ArticleDetail>;
  /** Fetch all categories, their subcategories and articles */
  categories: Array<CategoryListResult>;
  /** Fetch category, its subcategories and articles given an id */
  category?: Maybe<CategoryListResult>;
};


export type QueryUserSettingsArgs = {
  traits?: Maybe<Scalars['String']>;
  hash?: Maybe<Scalars['String']>;
  pageUrl: Scalars['URL'];
  initPageLoad: Scalars['Boolean'];
  loggedInOnly: Scalars['Boolean'];
  previewMode?: Scalars['Boolean'];
  enabled?: Maybe<Scalars['Boolean']>;
};


export type QueryKnowledgebaseArgs = {
  domain: Scalars['String'];
};


export type QueryWalkthroughsArgs = {
  pageUrl: Scalars['URL'];
};


export type QueryArticlesArgs = {
  pageUrl: Scalars['URL'];
  languageId: Scalars['LanguageID'];
  query: ArticleListQuery;
};


export type QuerySearchArticlesArgs = {
  pageUrl: Scalars['URL'];
  languageId: Scalars['LanguageID'];
  query: ArticleSearchQuery;
};


export type QuerySuggestArticlesArgs = {
  pageUrl: Scalars['URL'];
  languageId: Scalars['LanguageID'];
  query: ArticleSuggestionQuery;
};


export type QueryArticleArgs = {
  pageUrl: Scalars['URL'];
  languageId: Scalars['LanguageID'];
  id: Scalars['ID'];
};


export type QueryCategoriesArgs = {
  pageUrl: Scalars['URL'];
  languageId: Scalars['LanguageID'];
};


export type QueryCategoryArgs = {
  pageUrl: Scalars['URL'];
  languageId: Scalars['LanguageID'];
  id: Scalars['ID'];
};

/** Single sign on parameters */
export type Sso = {
  __typename?: 'SSO';
  /** Whether this KB supports SSO */
  enabled: Scalars['Boolean'];
  /** Whether this KB requires SSO */
  forced: Scalars['Boolean'];
  /** SSO login route */
  url?: Maybe<Scalars['String']>;
};

/** Smart group */
export type SmartGroup = {
  __typename?: 'SmartGroup';
  /** Smart group identifier */
  id: Scalars['ID'];
  /** Smart group name */
  name: Scalars['String'];
};


/** User attributes */
export type User = {
  __typename?: 'User';
  /** JWT for this session */
  token: Scalars['String'];
  /** Unique Elevio ID of the user */
  uid: Scalars['ID'];
};

/** Represents a set of user-specific settings for a company */
export type UserSettings = {
  __typename?: 'UserSettings';
  /** User attributes */
  user?: Maybe<User>;
  /** Company attributes */
  company: Company;
  /** Assistant settings */
  assistant: Assistant;
  /** Knowledge base settings */
  kb: KbUserSettings;
};

/** Represents a walkthrough */
export type Walkthrough = {
  __typename?: 'Walkthrough';
  /** Unique walkthrough ID */
  id: Scalars['ID'];
  /** Name of walkthrough */
  name: Scalars['String'];
  /** Whether this walkthrough can be skipped */
  skippable: Scalars['Boolean'];
  /** Whether this walkthrough can be resumed where the user left off */
  resumable: Scalars['Boolean'];
  /** Whether the user can go back to a previous step */
  backtrackable: Scalars['Boolean'];
  /** Whether the user can track the progress of the walkthrough */
  trackable: Scalars['Boolean'];
  /** The last step that was viewed */
  lastViewedStep?: Maybe<Scalars['Int']>;
  steps: Array<WalkthroughStep>;
  /** Timestamp of creation in UNIX epoch milliseconds */
  createdAt: Scalars['Long'];
  /** Timestamp of last update in UNIX epoch milliseconds */
  updatedAt: Scalars['Long'];
};

/** Represents a step in a walkthrough */
export type WalkthroughStep = {
  __typename?: 'WalkthroughStep';
  /** Order in which this step will be shown in the walkthrough */
  order: Scalars['Int'];
  /** Name of step for reference purposes */
  name: Scalars['String'];
  /** Order in which this step will be shown in the walkthrough */
  displayType: DisplayType;
  /** Width of step display in pixels */
  width: Scalars['Int'];
  /** CSS selectors of the DOM elements with which this step is associated */
  cssSelectors?: Maybe<Array<Scalars['String']>>;
  /** URL of multimedia asset for this step */
  mediaUrl?: Maybe<Scalars['String']>;
  /** Defines how the step is positioned */
  placement: WalkthroughStepPlacement;
  /** Multilingual translations for this step */
  translations: Array<WalkthroughStepTranslation>;
};


/** Represents a step in a walkthrough */
export type WalkthroughStepTranslationsArgs = {
  languageId: Scalars['LanguageID'];
};

/** Defines positioning properties of a walkthrough step */
export type WalkthroughStepPlacement = {
  __typename?: 'WalkthroughStepPlacement';
  /** Step position */
  step?: Maybe<PlacementType>;
  /** Anchor position */
  anchor: PlacementType;
  /** Beacon position */
  beacon: PlacementType;
  /** Beacon position offset */
  beaconOffset?: Maybe<PositionOffset>;
  /** Whether to target the inner text */
  targetInnerText: Scalars['Boolean'];
};

/** Represents a language translation for a walkthrough step */
export type WalkthroughStepTranslation = {
  __typename?: 'WalkthroughStepTranslation';
  /** Language code, e.g. en, fr, de, my, etc */
  languageId: Scalars['String'];
  /** Text that will appear in the step heading */
  title: Scalars['String'];
  /** Text that will appear in the step body */
  body: Scalars['String'];
  /** Text that will appear in the Next navigation button */
  next?: Maybe<Scalars['String']>;
  /** Text that will appear in the Back navigation button */
  back?: Maybe<Scalars['String']>;
};

/** Feedback form configuration */
export type FeedbackForm = {
  __typename?: 'feedbackForm';
  /** Whether this KB supports SSO */
  enabled: Scalars['Boolean'];
  /** Whether this KB requires SSO */
  emailFieldHidden: Scalars['Boolean'];
};


export type ArticlePageQueryVariables = Exact<{
  articleId: Scalars['ID'];
  pageUrl: Scalars['URL'];
  languageId: Scalars['LanguageID'];
  paging: PagingParams;
}>;


export type ArticlePageQuery = (
  { __typename?: 'Query' }
  & { article?: Maybe<(
    { __typename?: 'ArticleDetail' }
    & Pick<ArticleDetail, 'id' | 'title' | 'slug' | 'summary' | 'body' | 'bodyText' | 'featuredImageUrl' | 'createdAt' | 'lastUpdatedAt' | 'lastPublishedAt' | 'wordCount'>
    & { accessControl: (
      { __typename?: 'AccessControl' }
      & Pick<AccessControl, 'access' | 'isInternal'>
      & { smartGroups: Array<(
        { __typename?: 'SmartGroup' }
        & Pick<SmartGroup, 'id' | 'name'>
      )> }
    ), author: (
      { __typename?: 'Author' }
      & Pick<Author, 'id' | 'name' | 'avatar'>
    ), category: (
      { __typename?: 'CategoryListResult' }
      & Pick<CategoryListResult, 'id' | 'title' | 'slug' | 'featuredImageUrl'>
      & { parentCategory?: Maybe<(
        { __typename?: 'CategoryListResult' }
        & Pick<CategoryListResult, 'id' | 'title' | 'slug'>
      )>, subCategories: Array<(
        { __typename?: 'CategoryListResult' }
        & Pick<CategoryListResult, 'id' | 'title' | 'slug' | 'featuredImageUrl'>
        & { articles: (
          { __typename?: 'ArticleListResultSet' }
          & { results: Array<(
            { __typename?: 'ArticleListResult' }
            & Pick<ArticleListResult, 'id' | 'title' | 'slug' | 'summary'>
          )>, pageInfo: (
            { __typename?: 'PageInfo' }
            & Pick<PageInfo, 'totalCount'>
          ) }
        ) }
      )>, articles: (
        { __typename?: 'ArticleListResultSet' }
        & { results: Array<(
          { __typename?: 'ArticleListResult' }
          & Pick<ArticleListResult, 'id' | 'title' | 'slug' | 'summary'>
        )>, pageInfo: (
          { __typename?: 'PageInfo' }
          & Pick<PageInfo, 'totalCount'>
        ) }
      ) }
    ) }
  )>, relatedArticles: (
    { __typename?: 'ArticleListResultSet' }
    & { results: Array<(
      { __typename?: 'ArticleListResult' }
      & Pick<ArticleListResult, 'id' | 'title' | 'slug' | 'summary'>
      & { category: (
        { __typename?: 'CategorySearchResult' }
        & Pick<CategorySearchResult, 'id'>
      ) }
    )>, pageInfo: (
      { __typename?: 'PageInfo' }
      & Pick<PageInfo, 'totalCount'>
    ) }
  ) }
);

export type SearchQueryVariables = Exact<{
  query: ArticleSearchQuery;
  pageUrl: Scalars['URL'];
  languageId: Scalars['LanguageID'];
}>;


export type SearchQuery = (
  { __typename?: 'Query' }
  & { searchArticles: (
    { __typename?: 'ArticleSearchResultSet' }
    & { pageInfo: (
      { __typename?: 'PageInfo' }
      & Pick<PageInfo, 'totalCount' | 'currentPage' | 'totalPages'>
    ), results: Array<(
      { __typename?: 'ArticleSearchResult' }
      & Pick<ArticleSearchResult, 'id' | 'title' | 'slug'>
      & { highlights?: Maybe<(
        { __typename?: 'Highlights' }
        & Pick<Highlights, 'title' | 'body'>
      )>, category: (
        { __typename?: 'CategorySearchResult' }
        & Pick<CategorySearchResult, 'id' | 'title'>
        & { parentCategory?: Maybe<(
          { __typename?: 'CategorySearchResult' }
          & Pick<CategorySearchResult, 'id' | 'title' | 'slug'>
        )> }
      ) }
    )> }
  ) }
);

export type CategoryPageQueryVariables = Exact<{
  categoryId: Scalars['ID'];
  pageUrl: Scalars['URL'];
  languageId: Scalars['LanguageID'];
  paging: PagingParams;
}>;


export type CategoryPageQuery = (
  { __typename?: 'Query' }
  & { category?: Maybe<(
    { __typename?: 'CategoryListResult' }
    & Pick<CategoryListResult, 'id' | 'title' | 'slug' | 'featuredImageUrl'>
    & { parentCategory?: Maybe<(
      { __typename?: 'CategoryListResult' }
      & Pick<CategoryListResult, 'id' | 'title' | 'slug'>
    )>, subCategories: Array<(
      { __typename?: 'CategoryListResult' }
      & Pick<CategoryListResult, 'id' | 'title' | 'slug' | 'featuredImageUrl'>
      & { articles: (
        { __typename?: 'ArticleListResultSet' }
        & { results: Array<(
          { __typename?: 'ArticleListResult' }
          & Pick<ArticleListResult, 'id' | 'title' | 'slug' | 'summary'>
        )>, pageInfo: (
          { __typename?: 'PageInfo' }
          & Pick<PageInfo, 'totalCount'>
        ) }
      ) }
    )>, articles: (
      { __typename?: 'ArticleListResultSet' }
      & { results: Array<(
        { __typename?: 'ArticleListResult' }
        & Pick<ArticleListResult, 'id' | 'title' | 'slug' | 'summary'>
      )>, pageInfo: (
        { __typename?: 'PageInfo' }
        & Pick<PageInfo, 'totalCount'>
      ) }
    ) }
  )> }
);

export type DevServerQueryVariables = Exact<{ [key: string]: never; }>;


export type DevServerQuery = (
  { __typename?: 'Query' }
  & { userSettings: (
    { __typename?: 'UserSettings' }
    & { company: (
      { __typename?: 'Company' }
      & Pick<Company, 'name' | 'whitelabelEnabled'>
      & { languages: Array<(
        { __typename?: 'Language' }
        & Pick<Language, 'id'>
      )>, defaultLanguage: (
        { __typename?: 'Language' }
        & Pick<Language, 'id'>
      ) }
    ) }
  ) }
);

export type HomePageQueryVariables = Exact<{
  pageUrl: Scalars['URL'];
  languageId: Scalars['LanguageID'];
  paging: PagingParams;
}>;


export type HomePageQuery = (
  { __typename?: 'Query' }
  & { categories: Array<(
    { __typename?: 'CategoryListResult' }
    & Pick<CategoryListResult, 'id' | 'title' | 'slug' | 'featuredImageUrl'>
    & { parentCategory?: Maybe<(
      { __typename?: 'CategoryListResult' }
      & Pick<CategoryListResult, 'id' | 'title' | 'slug'>
    )>, subCategories: Array<(
      { __typename?: 'CategoryListResult' }
      & Pick<CategoryListResult, 'id' | 'title' | 'slug' | 'featuredImageUrl'>
      & { articles: (
        { __typename?: 'ArticleListResultSet' }
        & { results: Array<(
          { __typename?: 'ArticleListResult' }
          & Pick<ArticleListResult, 'id' | 'title' | 'slug' | 'summary'>
        )>, pageInfo: (
          { __typename?: 'PageInfo' }
          & Pick<PageInfo, 'totalCount'>
        ) }
      ) }
    )>, articles: (
      { __typename?: 'ArticleListResultSet' }
      & { results: Array<(
        { __typename?: 'ArticleListResult' }
        & Pick<ArticleListResult, 'id' | 'title' | 'slug' | 'summary'>
      )>, pageInfo: (
        { __typename?: 'PageInfo' }
        & Pick<PageInfo, 'totalCount'>
      ) }
    ) }
  )> }
);

export type KnowledgebaseQueryVariables = Exact<{
  domain: Scalars['String'];
}>;


export type KnowledgebaseQuery = (
  { __typename?: 'Query' }
  & { knowledgebase: (
    { __typename?: 'Knowledgebase' }
    & { company: (
      { __typename?: 'Company' }
      & Pick<Company, 'name' | 'uid' | 'whitelabelEnabled'>
      & { defaultLanguage: (
        { __typename?: 'Language' }
        & Pick<Language, 'id'>
      ), languages: Array<(
        { __typename?: 'Language' }
        & Pick<Language, 'id'>
      )> }
    ), settings: (
      { __typename?: 'KBSettings' }
      & Pick<KbSettings, 'subdomain' | 'cname' | 'siteUrl' | 'searchEngineIndexed' | 'customDomainEnabled' | 'favicon' | 'httpsEnabled' | 'companyManagedTls' | 'showEmbeddable' | 'searchAsYouTypeEnabled' | 'liveVersion' | 'whitelistEnabled' | 'agentLoginEnabled'>
      & { sso: (
        { __typename?: 'SSO' }
        & Pick<Sso, 'enabled' | 'forced' | 'url'>
      ), feedbackForm: (
        { __typename?: 'feedbackForm' }
        & Pick<FeedbackForm, 'enabled' | 'emailFieldHidden'>
      ) }
    ), redirects: Array<(
      { __typename?: 'KBRedirect' }
      & Pick<KbRedirect, 'fromUrl' | 'toUrl'>
    )>, accessWhitelist: Array<(
      { __typename?: 'KBWhitelistEntry' }
      & Pick<KbWhitelistEntry, 'cidrBlock'>
    )>, theme: (
      { __typename?: 'KBTheme' }
      & Pick<KbTheme, 'id' | 'name' | 'liveVersion'>
    ) }
  ) }
);

export type SitemapQueryVariables = Exact<{
  pageUrl: Scalars['URL'];
  languageId: Scalars['LanguageID'];
}>;


export type SitemapQuery = (
  { __typename?: 'Query' }
  & { categories: Array<(
    { __typename?: 'CategoryListResult' }
    & Pick<CategoryListResult, 'slug'>
    & { subCategories: Array<(
      { __typename?: 'CategoryListResult' }
      & Pick<CategoryListResult, 'slug'>
      & { articles: (
        { __typename?: 'ArticleListResultSet' }
        & { results: Array<(
          { __typename?: 'ArticleListResult' }
          & Pick<ArticleListResult, 'slug'>
        )> }
      ) }
    )>, articles: (
      { __typename?: 'ArticleListResultSet' }
      & { results: Array<(
        { __typename?: 'ArticleListResult' }
        & Pick<ArticleListResult, 'slug'>
      )> }
    ) }
  )> }
);

export type UserSettingsQueryVariables = Exact<{
  initPageLoad: Scalars['Boolean'];
  loggedInOnly: Scalars['Boolean'];
  pageUrl: Scalars['URL'];
}>;


export type UserSettingsQuery = (
  { __typename?: 'Query' }
  & { userSettings: (
    { __typename?: 'UserSettings' }
    & { user?: Maybe<(
      { __typename?: 'User' }
      & Pick<User, 'token'>
    )>, company: (
      { __typename?: 'Company' }
      & { defaultLanguage: (
        { __typename?: 'Language' }
        & Pick<Language, 'id'>
      ), languages: Array<(
        { __typename?: 'Language' }
        & Pick<Language, 'id'>
      )> }
    ) }
  ) }
);

export type UserTokenQueryVariables = Exact<{
  initPageLoad: Scalars['Boolean'];
  loggedInOnly: Scalars['Boolean'];
  pageUrl: Scalars['URL'];
  enabled: Scalars['Boolean'];
}>;


export type UserTokenQuery = (
  { __typename?: 'Query' }
  & { userSettings: (
    { __typename?: 'UserSettings' }
    & { user?: Maybe<(
      { __typename?: 'User' }
      & Pick<User, 'token'>
    )> }
  ) }
);
